import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Textarea } from '../../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Loader2, Send, MessageSquare, Lock, AlertCircle, Star } from 'lucide-react';
import { toast } from 'sonner';
import { CHANNELS, PLACEHOLDERS, FILTER_LABELS, FILTER_OPTIONS_BY_CAMPAIGN, FILTER_HINTS, REPEAT_OPTIONS } from './triggers';

const emptyForm = (trigger) => ({
  name: trigger.label, trigger: trigger.id, channel: 'whatsapp',
  message_template: '', subject: '', delay_hours: 0, delay_days: 0,
  inactive_days: 30, scheduled_for: '', is_active: true, template_vars: {},
  recipient_filter: 'all', recipient_service_id: '', repeat: 'once',
});

/** La data mostrata nel campo deve essere l'ora locale, non quella UTC salvata. */
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const defaultVars = (preset) => {
  const out = {};
  (preset?.custom_fields || []).forEach((f) => { out[f.key] = f.default || ''; });
  return out;
};

/** Crea o modifica una regola automatica per una sola tipologia. */
const AutomationFormDialog = ({ trigger, automation, preset, reviewUrl, open, onOpenChange, onSaved }) => {
  const { API, getAuthHeaders } = useAuth();
  const [form, setForm] = useState(() => {
    const base = emptyForm(trigger);
    if (!automation) return { ...base, template_vars: defaultVars(preset) };
    return {
      ...base, ...automation,
      template_vars: { ...defaultVars(preset), ...(automation.template_vars || {}) },
    };
  });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [services, setServices] = useState([]);

  const isWa = form.channel === 'whatsapp';
  const vars = form.template_vars || {};
  const isPromo = trigger.id === 'promotional';
  const byService = isPromo && form.recipient_filter === 'service';

  useEffect(() => {
    if (!byService || services.length) return;
    axios.get(`${API}/services`, getAuthHeaders())
      .then((res) => setServices((res.data || []).filter((s) => s.is_active !== false)))
      .catch(() => setServices([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byService]);

  const loadPreview = useCallback(async () => {
    if (!preset || (!isWa && !isPromo)) return;
    setPreviewLoading(true);
    try {
      const res = await axios.post(`${API}/marketing/whatsapp/campaign/preview`,
        {
          campaign_id: preset.id,
          filter: isPromo ? (form.recipient_filter || 'all') : preset.default_filter,
          custom_vars: vars,
          channel: form.channel,
          service_id: form.recipient_service_id || null,
        }, getAuthHeaders());
      setPreview(res.data);
    } catch {
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, isWa, isPromo, preset, form.channel, form.recipient_filter, form.recipient_service_id, JSON.stringify(vars)]);

  useEffect(() => {
    const t = setTimeout(loadPreview, 400);
    return () => clearTimeout(t);
  }, [loadPreview]);

  const setVar = (key, value) => setForm((f) => ({ ...f, template_vars: { ...f.template_vars, [key]: value } }));

  // Il modello approvato non parte se un campo resta vuoto: meglio dirlo prima
  const missingFields = isWa
    ? (preset?.custom_fields || []).filter((f) => !String(vars[f.key] ?? '').trim())
    : [];
  const canSave = (isWa ? (!!preset && missingFields.length === 0) : !!form.message_template)
    && (!byService || !!form.recipient_service_id);

  const save = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        trigger: trigger.id,
        message_template: isWa ? '' : form.message_template,
      };
      if (automation) await axios.put(`${API}/marketing/automations/${automation.id}`, payload, getAuthHeaders());
      else await axios.post(`${API}/marketing/automations`, payload, getAuthHeaders());
      toast.success(automation ? 'Regola aggiornata' : 'Regola creata');
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error('Salvataggio non riuscito', { description: err.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  const hoursTotal = (form.delay_days || 0) * 24 + (form.delay_hours || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="automation-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit">
            {automation ? 'Modifica' : 'Nuova'} regola · {trigger.label}
          </DialogTitle>
          <DialogDescription>{trigger.description}</DialogDescription>
        </DialogHeader>

        <form onSubmit={save} className="space-y-5 mt-2">
          <div className="space-y-2">
            <Label htmlFor="auto-name">Nome</Label>
            <Input id="auto-name" value={form.name} data-testid="automation-name-input"
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>

          <div className="space-y-2">
            <Label>Modalità di invio</Label>
            <div className="flex flex-wrap gap-2">
              {CHANNELS.map((ch) => (
                <button key={ch.id} type="button" onClick={() => setForm({ ...form, channel: ch.id })}
                  className={`px-4 min-h-[44px] rounded-lg border text-sm font-medium transition-all ${form.channel === ch.id ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:border-muted-foreground/30'}`}
                  data-testid={`channel-${ch.id}`}>
                  <span className={ch.color}>{ch.label}</span>
                </button>
              ))}
            </div>
          </div>

          {(trigger.id === 'post_appointment' || trigger.id === 'appointment_reminder') && (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{trigger.id === 'appointment_reminder' ? 'Anticipo (ore)' : 'Attesa (ore)'}</Label>
                  <Input type="number" min="0" value={form.delay_hours} data-testid="delay-hours-input"
                    onChange={(e) => setForm({ ...form, delay_hours: parseInt(e.target.value, 10) || 0 })} />
                </div>
                <div className="space-y-2">
                  <Label>{trigger.id === 'appointment_reminder' ? 'Anticipo (giorni)' : 'Attesa (giorni)'}</Label>
                  <Input type="number" min="0" value={form.delay_days} data-testid="delay-days-input"
                    onChange={(e) => setForm({ ...form, delay_days: parseInt(e.target.value, 10) || 0 })} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {hoursTotal === 0
                  ? (trigger.id === 'appointment_reminder'
                    ? 'Parte subito: la conferma arriva appena crei l\'appuntamento.'
                    : 'Parte appena l\'appuntamento finisce (controllo ogni ora).')
                  : `Parte ${hoursTotal} ore ${trigger.id === 'appointment_reminder' ? 'prima' : 'dopo'} l'appuntamento.`}
              </p>
            </div>
          )}

          {trigger.id === 'inactive_client' && (
            <div className="space-y-2">
              <Label>Dopo quanti giorni senza prenotare</Label>
              <Input type="number" min="7" value={form.inactive_days} data-testid="inactive-days-input"
                onChange={(e) => setForm({ ...form, inactive_days: parseInt(e.target.value, 10) || 30 })} />
            </div>
          )}

          {isPromo && (
            <>
              <div className="space-y-2">
                <Label>Filtro destinatari</Label>
                <Select value={form.recipient_filter || 'all'}
                  onValueChange={(v) => setForm({ ...form, recipient_filter: v })}>
                  <SelectTrigger data-testid="recipient-filter-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(FILTER_OPTIONS_BY_CAMPAIGN.promo || []).map((f) => (
                      <SelectItem key={f} value={f} data-testid={`recipient-filter-${f}`}>
                        {FILTER_LABELS[f] || f}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {FILTER_HINTS[form.recipient_filter] && (
                  <p className="text-xs text-muted-foreground" data-testid="filter-hint">
                    {FILTER_HINTS[form.recipient_filter]}
                  </p>
                )}
                {byService && (
                  <Select value={form.recipient_service_id || ''}
                    onValueChange={(v) => setForm({ ...form, recipient_service_id: v })}>
                    <SelectTrigger data-testid="recipient-service-select">
                      <SelectValue placeholder="Scegli il servizio" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((s) => (
                        <SelectItem key={s.id} value={s.id} data-testid={`recipient-service-${s.id}`}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <p className="text-xs text-muted-foreground" data-testid="recipient-count">
                  {byService && !form.recipient_service_id
                    ? 'Scegli un servizio per sapere quanti clienti riceveranno la promo.'
                    : preview
                      ? (preview.count === 1
                        ? '1 cliente rientra in questo gruppo oggi.'
                        : `${preview.count} clienti rientrano in questo gruppo oggi.`)
                      : 'Conto i clienti...'}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Quando parte</Label>
                <Input type="datetime-local" data-testid="promotional-schedule-input"
                  value={toLocalInput(form.scheduled_for)}
                  min={toLocalInput(new Date().toISOString())}
                  onChange={(e) => setForm({ ...form, scheduled_for: e.target.value ? new Date(e.target.value).toISOString() : '' })} />
              </div>
              <div className="space-y-2">
                <Label>Ripetizione</Label>
                <Select value={form.repeat || 'once'}
                  onValueChange={(v) => setForm({ ...form, repeat: v })}>
                  <SelectTrigger data-testid="repeat-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REPEAT_OPTIONS.map((r) => (
                      <SelectItem key={r.id} value={r.id} data-testid={`repeat-${r.id}`}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground" data-testid="repeat-hint">
                  {(form.repeat || 'once') === 'once'
                    ? 'Parte alla data scelta (ora italiana) e poi si spegne da sola.'
                    : `Parte alla data scelta e poi torna ${(form.repeat === 'weekly' ? 'ogni settimana, stesso giorno e stessa ora' : 'ogni mese, stesso giorno e stessa ora')}, finché non la spegni.`}
                </p>
              </div>
            </>
          )}

          {isWa ? (
            <div className="space-y-4 rounded-lg border border-green-600/30 bg-green-600/5 p-4" data-testid="whatsapp-template-section">
              <div className="flex items-start gap-2">
                <Lock className="w-4 h-4 text-green-700 dark:text-green-500 shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground">
                  Su WhatsApp il testo è quello del modello approvato
                  {preset && <> <span className="font-mono text-[11px] text-foreground">{preset.template_name}</span></>}:
                  non si può riscrivere, puoi solo compilare i campi qui sotto. È così che il messaggio arriva
                  anche a chi non ti scrive da giorni.
                </p>
              </div>

              {!preset && (
                <p className="text-sm text-destructive" data-testid="no-template-warning">
                  Per questa tipologia non esiste ancora un modello WhatsApp approvato: usa email o SMS.
                </p>
              )}

              {(preset?.custom_fields || []).map((f) => {
                const empty = !String(vars[f.key] ?? '').trim();
                return (
                  <div key={f.key} className="space-y-1.5">
                    <Label htmlFor={`var-${f.key}`}>
                      {f.label} <span className="text-destructive">*</span>
                    </Label>
                    <Input id={`var-${f.key}`}
                      type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                      value={vars[f.key] ?? ''}
                      onChange={(e) => setVar(f.key, e.target.value)}
                      className={empty ? 'border-destructive focus-visible:ring-destructive' : ''}
                      data-testid={`template-var-${f.key}`} />
                  </div>
                );
              })}

              {missingFields.length > 0 && (
                <div className="flex items-start gap-2 rounded-md bg-destructive/10 border border-destructive/30 p-2.5"
                  data-testid="missing-fields-warning">
                  <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                  <p className="text-xs text-destructive">
                    Il modello non parte finché non compili: {missingFields.map((f) => f.label).join(', ')}.
                  </p>
                </div>
              )}

              {preset && (preset.custom_fields || []).length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Questo modello non ha campi da compilare: nome cliente, salone e dati dell'appuntamento
                  vengono inseriti da soli.
                </p>
              )}

              {preset && (
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-2 text-xs">
                    <MessageSquare className="w-3.5 h-3.5" />Anteprima del messaggio
                  </Label>
                  <div className="rounded-lg bg-background border p-3 text-sm whitespace-pre-wrap min-h-[64px]"
                    data-testid="whatsapp-preview">
                    {previewLoading && !preview
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : preview?.preview_text
                        ? preview.preview_text
                        : <span className="text-muted-foreground text-xs">
                          Anteprima non disponibile: il collegamento WhatsApp non è attivo, quindi non
                          possiamo leggere il modello approvato da Meta.
                        </span>}
                  </div>
                  {preview?.sample_customer && (
                    <p className="text-[11px] text-muted-foreground">
                      Esempio costruito sui dati di <span className="font-medium text-foreground">{preview.sample_customer}</span>.
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <>
              {form.channel === 'email' && (
                <div className="space-y-2">
                  <Label htmlFor="auto-subject">Oggetto dell'email</Label>
                  <Input id="auto-subject" value={form.subject} data-testid="automation-subject-input"
                    onChange={(e) => setForm({ ...form, subject: e.target.value })} />
                </div>
              )}
              <div className="space-y-2">
                <Label>Messaggio</Label>
                <div className="flex flex-wrap gap-1">
                  {PLACEHOLDERS.map((p) => (
                    <button key={p.tag} type="button" title={p.desc}
                      onClick={() => setForm((f) => ({ ...f, message_template: (f.message_template || '') + p.tag }))}
                      className="px-2 py-1 text-xs rounded-md bg-muted hover:bg-muted-foreground/10 text-muted-foreground transition-colors">
                      {p.tag}
                    </button>
                  ))}
                </div>
                <Textarea rows={5} value={form.message_template || ''} data-testid="automation-message-input"
                  placeholder={trigger.template}
                  onChange={(e) => setForm({ ...form, message_template: e.target.value })} />
                {trigger.id === 'post_appointment' && (
                  <div className="flex items-start gap-2 rounded-md bg-muted/60 p-2.5" data-testid="review-link-hint">
                    <Star className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    {reviewUrl ? (
                      <p className="text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">{'{recensione}'}</span> diventa il tuo link
                        Google: <span className="break-all">{reviewUrl}</span>. Se lo togli dal testo lo aggiungiamo
                        comunque in fondo al messaggio.
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Nessun link recensione impostato: aggiungilo in <strong>Google Business</strong> e il
                        segnaposto <span className="font-medium text-foreground">{'{recensione}'}</span> si compilerà da solo.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} data-testid="automation-active-switch"
                onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              <Label className="text-sm">Attiva subito</Label>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="min-h-[44px]" onClick={() => onOpenChange(false)}>Annulla</Button>
              <Button type="submit" disabled={saving || !canSave}
                className="min-h-[44px]" data-testid="save-automation-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {automation ? 'Aggiorna' : 'Crea'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AutomationFormDialog;
